import { LeftOutlined, QuestionCircleOutlined, RightOutlined } from '@ant-design/icons';
import { Button, message, Modal, notification, Popover } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';
import { CommonReduxState } from '@src/models/commons/reduxState.interface';
import type { CreateShiftChangesParams } from '@src/models/interfaces/attendanceEvent.interface';
import type {
  IWorkJourneyManagementMatrixListResponse,
  IWorkJourneyMatrixCell,
} from '@src/models/interfaces/workJourneyManagement.interface';
import { getFullUrl } from '@src/packages/utils/functions';
import { DateUtil } from '@src/packages/utils/dates';
import { set as setSessionStorage } from '@src/packages/utils/sessionStorage';
import { getRouteToWorkJourneyMonthMatrix, PATHS } from '@src/packages/workJourney/constants';
import { PlanMonthlyJourneyDrawer } from '@src/packages/workJourney/screens/WorkJourneyManagement/components/PlanMonthlyJourneyDrawer';
import {
  WORK_JOURNEY_MANAGEMENT_ACTIVE_TAB_STORAGE_KEY,
  WORK_JOURNEY_MANAGEMENT_DEFAULT_TAB,
} from '@src/packages/workJourney/screens/WorkJourneyManagement/constants';
import WorkJourneyManagementService from '@src/services/WorkJourneyManagementService';
import IntlMessages from '@src/util/IntlMessages';
import { getMatrixCreatePastDayErrorMessageId } from '@src/util/sentry/isExpectedWorkJourneyMatrixCreateError';

import MatrixEditAttendanceDrawer from './components/MatrixEditAttendanceDrawer';
import { WorkJourneyMatrixAbsenceDrawer } from './components/WorkJourneyMatrixAbsenceDrawer';
import WorkJourneyMatrixAttendanceDrawer from './components/WorkJourneyMatrixAttendanceDrawer';
import { WorkJourneyMatrixDayInfoDrawer } from './components/WorkJourneyMatrixDayInfoDrawer';
import {
  selectionKey,
  WorkJourneyMonthMatrixGrid,
  type MatrixSelectionKey,
} from './components/WorkJourneyMonthMatrixGrid';
import { WorkJourneyMatrixReplaceDrawer } from './components/WorkJourneyMatrixReplaceDrawer';
import { WorkJourneyMatrixSelectionBar } from './components/WorkJourneyMatrixSelectionBar';
import { WorkJourneyMatrixVacationDrawer } from './components/WorkJourneyMatrixVacationDrawer';
import {
  useWorkJourneyMatrixAttendanceDrawer,
  type MatrixAttendanceDrawerData,
} from './hooks/useWorkJourneyMatrixAttendanceDrawer';
import { useWorkJourneyMatrixSelectionActions } from './hooks/useWorkJourneyMatrixSelectionActions';
import { applyShiftChangesToMatrixCells } from './utils/applyOptimisticAttendanceEdit';
import { applyFutureMatrixClickSelection } from './utils/applyFutureMatrixClickSelection';
import {
  buildFutureMatrixRangeKeys,
  toggleFutureMatrixSelectionKey,
} from './utils/buildFutureMatrixRangeSelection';
import { buildCreatedPastDayMatrixCell } from './utils/buildCreatedPastDayMatrixCell';
import {
  getMatrixSelectionMeta,
  shouldShowMatrixSelectionBar,
} from './utils/getMatrixSelectionMeta';
import { isFutureMatrixCalendarDate } from './utils/isFutureMatrixCalendarDate';
import { buildMatrixReplacementExcludeUserIds } from './utils/buildMatrixReplacementExcludeUserIds';
import { isMatrixCoverOnlyUser } from './utils/isMatrixCoverOnlyUser';
import { isRealAttendanceRecordId } from './utils/resolveAttendanceAddress';
import { shouldCreateReplacementAttendance } from './utils/shouldCreateReplacementAttendance';
import { isMatrixRestDayVisual } from './utils/matrixCellDisplay';
import {
  buildMatrixRestDateSet,
  canCreateBlankPastDayAttendance,
} from './utils/matrixRestDays';
import { recomputeMatrixDayCoverage } from './utils/recomputeMatrixDayCoverage';
import {
  estimateDailyShiftHours,
  recomputeMatrixProgrammedHours,
  resolveShiftBreakMinutes,
} from './utils/recomputeMatrixProgrammedHours';
import { collapseSortedDatesToRanges } from './utils/resolveMatrixExceptionDateRanges';
import styles from './styles.module.less';

type MatrixShiftHoursParams = {
  daysOfWeek?: Array<string | number> | null;
  /** null = snapshot not ready; skip hours recompute so server totals stay. */
  dailyEstimateHours: number | null;
  breakMinutes: number;
};

const withRecomputedMatrixDerived = (
  previous: IWorkJourneyManagementMatrixListResponse,
  cells: IWorkJourneyMatrixCell[],
  { daysOfWeek, dailyEstimateHours, breakMinutes }: MatrixShiftHoursParams,
): IWorkJourneyManagementMatrixListResponse => {
  const usersWithHours =
    dailyEstimateHours == null
      ? previous.users
      : recomputeMatrixProgrammedHours({
          users: previous.users,
          cells,
          dailyEstimate: dailyEstimateHours,
          breakMinutes,
        });
  // Drop cover-only rows that no longer have any chip (e.g. REM removed with absence).
  const userIdsWithCells = new Set(cells.map(cell => cell.userId).filter(Boolean));
  const users = usersWithHours.filter(
    user => user.isMember !== false || userIdsWithCells.has(user.id),
  );

  return {
    ...previous,
    cells,
    users,
    coverage: recomputeMatrixDayCoverage({
      days: previous.days,
      cells,
      users,
      requiredMembers: previous.config.requiredMembers ?? 0,
      daysOfWeek,
      holidayDates: previous.holidayDates,
    }),
  };
};

const service = new WorkJourneyManagementService();

/** Legend chips: DT glossary shorts + product PRE/REM. */
const LEGEND_ITEMS = [
  {
    code: 'WORK',
    label: '✓',
    chipClass: styles.legendWork,
    id: 'workJourney.management.matrix.legend.work',
  },
  {
    code: 'D',
    label: 'D',
    chipClass: styles.legendRest,
    id: 'workJourney.management.matrix.legend.rest',
  },
  {
    code: 'PRE',
    label: 'PRE',
    chipClass: styles.legendPre,
    id: 'workJourney.management.matrix.legend.pre',
  },
  {
    code: 'AUJ',
    label: 'AUJ',
    chipClass: styles.legendAuj,
    id: 'work.direction.glossary.item.AJ',
  },
  {
    code: 'AUI',
    label: 'AUI',
    chipClass: styles.legendAui,
    id: 'work.direction.glossary.item.AI',
  },
  {
    code: 'VAC',
    label: 'VAC',
    chipClass: styles.legendVac,
    id: 'work.direction.glossary.item.VAC',
  },
  {
    code: 'REM',
    label: 'REM',
    chipClass: styles.legendRem,
    id: 'workJourney.management.matrix.legend.rem',
  },
  {
    code: 'REM_OUT',
    label: 'REM',
    chipClass: styles.legendRemOut,
    id: 'workJourney.management.matrix.legend.remOut',
  },
  {
    code: 'BLANK',
    label: '',
    chipClass: styles.legendBlank,
    id: 'workJourney.management.matrix.legend.blank',
  },
] as const;

const shiftMonthYear = (month: number, year: number, delta: number) => {
  const date = new Date(year, month - 1 + delta, 1);
  return { month: date.getMonth() + 1, year: date.getFullYear() };
};

interface MatrixRouteParams {
  workJourneyConfigId: string;
  year: string;
  month: string;
}

export const WorkJourneyMonthMatrix = () => {
  const intl = useIntl();
  const history = useHistory();
  const { url } = useRouteMatch();
  const {
    workJourneyConfigId,
    year: yearParam,
    month: monthParam,
  } = useParams<MatrixRouteParams>();
  const orgId = useSelector(({ organizations }: CommonReduxState) => organizations.organization.id);

  const month = Number(monthParam);
  const year = Number(yearParam);

  const [data, setData] = useState<IWorkJourneyManagementMatrixListResponse | null>(null);
  const [daysOfWeek, setDaysOfWeek] = useState<Array<string | number> | null>(null);
  /** From config workShift; null until snapshot resolves (or fails). */
  const [dailyEstimateHours, setDailyEstimateHours] = useState<number | null>(null);
  const [shiftBreakMinutes, setShiftBreakMinutes] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<MatrixSelectionKey>>(new Set());
  const {
    open: openAttendanceDrawer,
    close: closeAttendanceDrawer,
    target: attendanceTarget,
    data: attendanceData,
    loading: attendanceLoading,
    error: attendanceError,
    isOpen: attendanceOpen,
    applyPendingChanges: applyPendingAttendanceChanges,
  } = useWorkJourneyMatrixAttendanceDrawer({
    organizationId: orgId,
    workJourneyConfigId,
  });

  const [planDrawerOpen, setPlanDrawerOpen] = useState(false);
  const [editAttendanceData, setEditAttendanceData] = useState<MatrixAttendanceDrawerData | null>(
    null,
  );
  const [editAttendanceOpen, setEditAttendanceOpen] = useState(false);

  const dragRef = useRef<{
    active: boolean;
    moved: boolean;
    /** ⌘/Ctrl toggles are selection-only; never open the attendance drawer. */
    suppressClick: boolean;
    /**
     * Future plain-click seeded a fresh single-day selection on mousedown —
     * skip the click toggle so the day stays selected.
     */
    seededFutureClick: boolean;
    anchorUserId: string | null;
    anchorDate: string | null;
  }>({
    active: false,
    moved: false,
    suppressClick: false,
    seededFutureClick: false,
    anchorUserId: null,
    anchorDate: null,
  });
  const selectedKeysRef = useRef(selectedKeys);
  selectedKeysRef.current = selectedKeys;

  const cellByKey = useMemo(() => {
    const map = new Map<string, NonNullable<typeof data>['cells'][number]>();
    data?.cells.forEach(cell => map.set(selectionKey(cell.userId, cell.date), cell));
    return map;
  }, [data]);

  const restDates = useMemo(
    () => buildMatrixRestDateSet(data?.days ?? [], data?.cells ?? [], daysOfWeek),
    [data?.cells, data?.days, daysOfWeek],
  );

  const userById = useMemo(() => {
    const map = new Map<string, NonNullable<typeof data>['users'][number]>();
    data?.users.forEach(user => map.set(user.id, user));
    return map;
  }, [data]);

  const selectionMeta = useMemo(() => getMatrixSelectionMeta(selectedKeys), [selectedKeys]);
  const selectedUser = selectionMeta ? (userById.get(selectionMeta.userId) ?? null) : null;
  const replacementExcludeUserIds = useMemo(
    () => buildMatrixReplacementExcludeUserIds(data?.users ?? [], selectedUser?.id),
    [data?.users, selectedUser?.id],
  );
  const selectionExceptionRanges = useMemo(
    () => (selectionMeta ? collapseSortedDatesToRanges(selectionMeta.dates) : []),
    [selectionMeta],
  );
  const lockDrawerPeriodToSelection = Boolean(selectionMeta && !selectionMeta.isContiguous);

  const clearSelection = useCallback(() => {
    const next = new Set<MatrixSelectionKey>();
    selectedKeysRef.current = next;
    setSelectedKeys(next);
  }, []);

  const openAttendanceForCell = useCallback(
    (userId: string, date: string) => {
      const user = userById.get(userId);
      if (!user) return;
      const cell = cellByKey.get(selectionKey(userId, date));
      openAttendanceDrawer({
        attendanceRecordId: cell?.attendanceRecordId,
        userId,
        date,
        user,
        cell: cell
          ? {
              kind: cell.kind,
              code: cell.code,
              exceptionType: cell.exceptionType,
              leaveType: cell.leaveType,
              observation: cell.observation,
              attendanceCompleted: cell.attendanceCompleted,
              description: cell.description,
              attendance: cell.attendance,
            }
          : null,
      });
    },
    [cellByKey, openAttendanceDrawer, userById],
  );

  const creatingPastDayRef = useRef(false);

  const performCreatePastDayAttendance = useCallback(
    async (userId: string, date: string) => {
      if (!orgId || !workJourneyConfigId || creatingPastDayRef.current) return;
      const user = userById.get(userId);
      if (!user) return;

      creatingPastDayRef.current = true;
      const hideLoading = message.loading(
        intl.formatMessage({ id: 'workJourney.management.matrix.createPastDay.loading' }),
        0,
      );
      try {
        const result = await service.createMatrixAttendance({
          organizationId: orgId,
          workJourneyConfigId,
          userId,
          date,
        });

        const existingCell = cellByKey.get(selectionKey(userId, date));
        const createdCell =
          existingCell && shouldCreateReplacementAttendance(existingCell)
            ? {
                ...existingCell,
                attendanceRecordId: result.attendanceRecordId,
              }
            : buildCreatedPastDayMatrixCell({
              userId,
              date,
              attendanceRecordId: result.attendanceRecordId,
              displayName: user.displayName,
              idCard: user.idCard,
            });

        setData(previous => {
          if (!previous) return previous;
          const key = selectionKey(userId, date);
          const without = previous.cells.filter(
            cell => selectionKey(cell.userId, cell.date) !== key,
          );
          return withRecomputedMatrixDerived(previous, [...without, createdCell], {
            daysOfWeek,
            dailyEstimateHours,
            breakMinutes: shiftBreakMinutes,
          });
        });

        openAttendanceDrawer({
          attendanceRecordId: result.attendanceRecordId,
          userId,
          date,
          user,
          cell: {
            kind: createdCell.kind,
            code: createdCell.code,
            exceptionType: createdCell.exceptionType,
            leaveType: createdCell.leaveType,
            observation: createdCell.observation,
            attendanceCompleted: createdCell.attendanceCompleted,
            description: createdCell.description,
            attendance: createdCell.attendance,
          },
        });

        if (result.created) {
          message.success(
            intl.formatMessage({ id: 'workJourney.management.matrix.createPastDay.success' }),
          );
        }
      } catch (err) {
        notification.error({
          message: intl.formatMessage({
            id: getMatrixCreatePastDayErrorMessageId(err),
          }),
        });
      } finally {
        hideLoading();
        creatingPastDayRef.current = false;
      }
    },
    [
      cellByKey,
      dailyEstimateHours,
      daysOfWeek,
      intl,
      openAttendanceDrawer,
      orgId,
      shiftBreakMinutes,
      userById,
      workJourneyConfigId,
    ],
  );

  const handleCreatePastDayAttendance = useCallback(
    (userId: string, date: string) => {
      if (!orgId || !workJourneyConfigId || creatingPastDayRef.current) return;
      const user = userById.get(userId);
      if (!user) return;

      const targetCell = cellByKey.get(selectionKey(userId, date));
      const isCoverageCreate = shouldCreateReplacementAttendance(targetCell);
      if (!isCoverageCreate && isMatrixCoverOnlyUser(user)) {
        notification.warning({
          message: intl.formatMessage({
            id: 'workJourney.management.matrix.createPastDay.notAMember',
          }),
        });
        return;
      }

      // Coverage REM without an AR is a data gap from applyShiftReplacement —
      // create silently and open markings (no confirm friction).
      if (isCoverageCreate) {
        void performCreatePastDayAttendance(userId, date);
        return;
      }

      Modal.confirm({
        centered: true,
        title: intl.formatMessage({
          id: 'workJourney.management.matrix.createPastDay.confirmTitle',
        }),
        content: intl.formatMessage(
          {
            id: 'workJourney.management.matrix.createPastDay.confirmContent',
          },
          {
            name: user.displayName,
            date:
              DateUtil.toDateString(
                DateUtil.getCalendarDateBoundsMillis(date)?.startMillis,
                DateUtil.DEFAULT_DATE_FORMAT,
              ) || date,
          },
        ),
        okText: intl.formatMessage({ id: 'button.create' }),
        cancelText: intl.formatMessage({ id: 'button.cancel' }),
        onOk: () => performCreatePastDayAttendance(userId, date),
      });
    },
    [
      cellByKey,
      intl,
      orgId,
      performCreatePastDayAttendance,
      userById,
      workJourneyConfigId,
    ],
  );

  const handleOpenEditAttendance = useCallback((drawerData: MatrixAttendanceDrawerData) => {
    setEditAttendanceData(drawerData);
    setEditAttendanceOpen(true);
  }, []);

  const handleCloseEditAttendance = useCallback(() => {
    setEditAttendanceOpen(false);
    setEditAttendanceData(null);
  }, []);

  const loadMatrixRequestIdRef = useRef(0);

  const loadMatrix = useCallback(async () => {
    if (!orgId || !workJourneyConfigId || !month || !year) return;
    const requestId = ++loadMatrixRequestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const [response, configSnapshot] = await Promise.all([
        service.listMonthMatrix({
          organizationId: orgId,
          workJourneyConfigId,
          month,
          year,
        }),
        service.getWorkJourneyConfigSnapshot(orgId, workJourneyConfigId).catch(() => null),
      ]);
      if (requestId !== loadMatrixRequestIdRef.current) return;
      setData(response);
      setDaysOfWeek(configSnapshot?.workShift?.daysOfWeek ?? null);
      if (configSnapshot?.workShift) {
        setDailyEstimateHours(estimateDailyShiftHours(configSnapshot.workShift));
        setShiftBreakMinutes(resolveShiftBreakMinutes(configSnapshot.workShift));
      } else {
        setDailyEstimateHours(null);
        setShiftBreakMinutes(0);
      }
      setSelectedKeys(new Set());
    } catch (err) {
      if (requestId !== loadMatrixRequestIdRef.current) return;
      setError(err instanceof Error ? err.message : String(err));
      setData(null);
      setDaysOfWeek(null);
      setDailyEstimateHours(null);
      setShiftBreakMinutes(0);
    } finally {
      if (requestId === loadMatrixRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [orgId, workJourneyConfigId, month, year]);

  /**
   * The edit is applied by a Pub/Sub trigger, so reloading here would bring back
   * pre-edit data. Patch the loaded matrix and drawer instead; the next month
   * load (or revisit) picks up server truth.
   *
   * Identity comes from the attendance drawer target; fall back to the edited
   * record's employee so a close race cannot skip the optimistic patch.
   */
  const handleEditAttendanceSuccess = useCallback(
    (changes: CreateShiftChangesParams) => {
      const userId = attendanceTarget?.userId ?? editAttendanceData?.record.employeeId;
      const date = attendanceTarget?.date;
      if (!userId || !date) return;

      setData(previous =>
        previous
          ? withRecomputedMatrixDerived(
              previous,
              applyShiftChangesToMatrixCells(previous.cells, { userId, date }, changes),
              {
                daysOfWeek,
                dailyEstimateHours,
                breakMinutes: shiftBreakMinutes,
              },
            )
          : previous,
      );
      applyPendingAttendanceChanges(changes);
    },
    [
      applyPendingAttendanceChanges,
      attendanceTarget,
      dailyEstimateHours,
      daysOfWeek,
      editAttendanceData?.record.employeeId,
      shiftBreakMinutes,
    ],
  );

  const patchMatrixCells = useCallback(
    (patcher: (cells: IWorkJourneyMatrixCell[]) => IWorkJourneyMatrixCell[]) => {
      setData(previous =>
        previous
          ? withRecomputedMatrixDerived(previous, patcher(previous.cells), {
              daysOfWeek,
              dailyEstimateHours,
              breakMinutes: shiftBreakMinutes,
            })
          : previous,
      );
    },
    [dailyEstimateHours, daysOfWeek, shiftBreakMinutes],
  );

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  const selectionActions = useWorkJourneyMatrixSelectionActions({
    organizationId: orgId,
    workJourneyConfigId,
    selectionMeta,
    selectedUser,
    cellByKey,
    makeSelectionKey: selectionKey,
    clearSelection,
    patchMatrixCells,
    reloadMatrix: loadMatrix,
  });

  const handleBack = () => {
    history.push(getFullUrl(PATHS.WORK_JOURNEY_MANAGEMENT, url));
  };

  const navigateMonth = (delta: number) => {
    const next = shiftMonthYear(month, year, delta);
    history.push(
      getFullUrl(
        getRouteToWorkJourneyMonthMatrix({
          workJourneyConfigId,
          year: next.year,
          month: next.month,
        }),
        url,
      ),
    );
  };

  const monthLabel = intl.formatDate(new Date(year, month - 1, 1), {
    month: 'long',
    year: 'numeric',
  });

  const configDeleted = data?.configDeleted === true;

  const handleCellMouseDown = (userId: string, date: string, event: MouseEvent) => {
    // Keep default so the subsequent `click` still fires (needed to open the drawer).
    if (event.button !== 0) return;
    if (configDeleted) return;
    const key = selectionKey(userId, date);

    // Multi-select modifiers only apply to future days (platform actions are future-only).
    if (event.metaKey || event.ctrlKey) {
      if (!isFutureMatrixCalendarDate(date)) {
        return;
      }
      setSelectedKeys(prev => {
        const next = toggleFutureMatrixSelectionKey(prev, userId, date, selectionKey);
        selectedKeysRef.current = next;
        return next;
      });
      dragRef.current = {
        active: false,
        moved: false,
        suppressClick: true,
        seededFutureClick: false,
        anchorUserId: null,
        anchorDate: null,
      };
      return;
    }

    if (event.shiftKey && dragRef.current.anchorUserId === userId && dragRef.current.anchorDate) {
      const anchor = dragRef.current.anchorDate;
      // Shift-range multi-select requires a future anchor; past anchors stay single-click.
      if (!isFutureMatrixCalendarDate(anchor)) {
        return;
      }
      const dates = data?.days ?? [];
      const rangeKeys = buildFutureMatrixRangeKeys(
        userId,
        dates,
        anchor,
        date,
        selectionKey,
      );
      if (rangeKeys.length === 0) {
        return;
      }
      const next = new Set(rangeKeys);
      selectedKeysRef.current = next;
      setSelectedKeys(next);
      dragRef.current = {
        ...dragRef.current,
        active: false,
        moved: rangeKeys.length > 1,
        suppressClick: rangeKeys.length > 1,
        seededFutureClick: false,
      };
      return;
    }

    // Future days: click-add on the same user; other user starts clean.
    // Keep existing same-user selection on mousedown so click can toggle-add.
    if (isFutureMatrixCalendarDate(date)) {
      const meta = getMatrixSelectionMeta(selectedKeysRef.current);
      const startsClean = !meta || meta.userId !== userId;
      if (startsClean) {
        const next = new Set<MatrixSelectionKey>([key]);
        selectedKeysRef.current = next;
        setSelectedKeys(next);
      }
      dragRef.current = {
        active: true,
        moved: false,
        suppressClick: false,
        seededFutureClick: startsClean,
        anchorUserId: userId,
        anchorDate: date,
      };
      return;
    }

    // Past/today: single-cell only (attendance drawer). Do not start a drag range.
    dragRef.current = {
      active: false,
      moved: false,
      suppressClick: false,
      seededFutureClick: false,
      anchorUserId: userId,
      anchorDate: date,
    };
    const next = new Set<MatrixSelectionKey>([key]);
    selectedKeysRef.current = next;
    setSelectedKeys(next);
  };

  const handleCellMouseEnter = (userId: string, date: string, _event: MouseEvent) => {
    if (!dragRef.current.active || dragRef.current.anchorUserId !== userId) return;
    const anchor = dragRef.current.anchorDate;
    if (!anchor || !data?.days) return;
    // Drag multi-select is future-only; past anchors never become ranges.
    if (!isFutureMatrixCalendarDate(anchor)) return;
    if (anchor !== date) {
      dragRef.current.moved = true;
    }
    const rangeKeys = buildFutureMatrixRangeKeys(
      userId,
      data.days,
      anchor,
      date,
      selectionKey,
    );
    if (rangeKeys.length === 0) return;
    const next = new Set(rangeKeys);
    selectedKeysRef.current = next;
    setSelectedKeys(next);
  };

  const handleCellMouseUp = () => {
    dragRef.current = {
      ...dragRef.current,
      active: false,
    };
  };

  const handleCellClick = (userId: string, date: string) => {
    const { moved, suppressClick, seededFutureClick } = dragRef.current;
    dragRef.current.moved = false;
    dragRef.current.suppressClick = false;
    dragRef.current.seededFutureClick = false;

    // Multi-day drag / ⌘ selection uses the floating action bar — not the attendance drawer.
    if (moved || suppressClick) {
      return;
    }

    if (configDeleted) {
      const historicalCell = cellByKey.get(selectionKey(userId, date));
      if (!isRealAttendanceRecordId(historicalCell?.attendanceRecordId)) {
        return;
      }
      openAttendanceForCell(userId, date);
      return;
    }

    // Future days: click-add / click-remove for one user (no attendance drawer).
    if (isFutureMatrixCalendarDate(date)) {
      if (seededFutureClick) {
        return;
      }
      setSelectedKeys(prev => {
        const next = applyFutureMatrixClickSelection(prev, userId, date, selectionKey);
        selectedKeysRef.current = next;
        return next;
      });
      return;
    }

    if (selectedKeysRef.current.size > 1) {
      return;
    }

    // Empty (white) past/today work cells: create an AR when the user has no
    // other-journey record that day, then open the drawer to mark presence/leave.
    // Rest days always have a D cell for members — skip non-work blanks.
    // Wait for workShift.daysOfWeek before treating blanks as creatable.
    if (!cellByKey.has(selectionKey(userId, date))) {
      if (!canCreateBlankPastDayAttendance(date, daysOfWeek)) {
        return;
      }
      clearSelection();
      void handleCreatePastDayAttendance(userId, date);
      return;
    }

    // Journey rest days ("D"): never open the attendance marking drawer.
    const cell = cellByKey.get(selectionKey(userId, date));
    if (isMatrixRestDayVisual(cell, { restDates })) {
      return;
    }

    // Coverage day with no attendance record yet: create one, then open markings.
    // An existing record (including the substitute's original journey) is opened as-is.
    if (shouldCreateReplacementAttendance(cell)) {
      clearSelection();
      void handleCreatePastDayAttendance(userId, date);
      return;
    }

    // Single-day click (today/past): keep the cell highlighted and open attendance marking.
    openAttendanceForCell(userId, date);
  };

  const handleScheduleAbsence = () => {
    if (!selectionMeta || !selectedUser) {
      message.info(intl.formatMessage({ id: 'workJourney.management.matrix.selectDaysHint' }));
      return;
    }
    // Use the matrix absence drawer (keeps selection context) instead of the
    // cover-absence route, which requires Attention `location.state`.
    selectionActions.handleBarAction('justified_absence');
  };

  const headerActionButtons = [
    {
      label: intl.formatMessage({ id: 'workJourney.management.actions.fixIssues' }),
      type: 'secondary' as const,
      action: () => {
        setSessionStorage(
          WORK_JOURNEY_MANAGEMENT_ACTIVE_TAB_STORAGE_KEY,
          WORK_JOURNEY_MANAGEMENT_DEFAULT_TAB,
        );
        history.push(getFullUrl(PATHS.WORK_JOURNEY_MANAGEMENT, url));
      },
    },
    ...(configDeleted
      ? []
      : [
          {
            label: intl.formatMessage({ id: 'workJourney.management.actions.scheduleAbsence' }),
            type: 'secondary' as const,
            action: handleScheduleAbsence,
          },
          {
            label: intl.formatMessage({ id: 'workJourney.management.actions.plan' }),
            type: 'primary' as const,
            action: () => setPlanDrawerOpen(true),
          },
        ]),
  ];

  const subtitle = useMemo(() => {
    if (!data?.config) return null;
    const shift = data.config.shiftDescription || '';
    const minAssigned = intl.formatMessage(
      { id: 'workJourney.management.matrix.minAssigned' },
      {
        min: data.config.requiredMembers ?? 0,
        assigned: data.config.assignedMembers ?? 0,
      },
    );
    return [shift, minAssigned].filter(Boolean).join(' · ');
  }, [data?.config, intl]);

  return (
    <BoxContainer>
      <BoxContainer fullContent fixed bottomBorder>
        <FilterContainer
          goBack={handleBack}
          title={
            <div className={styles.titleBlock}>
              <Title.Header
                value={data?.config.name || <IntlMessages id="workJourney.management.title" />}
              />
              {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
            </div>
          }
          actionButtons={headerActionButtons}
        />
      </BoxContainer>

      <BoxContainer
        fullContent
        loading={loading}
        error={error}
        contentClassName={
          shouldShowMatrixSelectionBar(selectionMeta) && selectedUser
            ? styles.withSelectionBar
            : undefined
        }
      >
        <div className={styles.toolbar}>
          <div className={styles.monthNav}>
            <Button
              className="gx-mb-0"
              type="text"
              icon={<LeftOutlined />}
              onClick={() => navigateMonth(-1)}
              aria-label="Previous month"
            />
            <span className={styles.monthLabel}>{monthLabel}</span>
            <Button
              className="gx-mb-0"
              type="text"
              icon={<RightOutlined />}
              onClick={() => navigateMonth(1)}
              aria-label="Next month"
            />
          </div>
          <div className={styles.legend}>
            {LEGEND_ITEMS.map(item => (
              <span key={item.code} className={styles.legendItem}>
                <span className={`${styles.legendChip} ${item.chipClass}`}>{item.label}</span>
                <IntlMessages id={item.id} />
              </span>
            ))}
            {!configDeleted && (
              <Popover
                placement="bottomRight"
                trigger="click"
                overlayClassName={styles.selectionHelpPopover}
                title={
                  <IntlMessages id="workJourney.management.matrix.selectionHint.title" />
                }
                content={
                  <div className={styles.selectionHelpContent}>
                    <IntlMessages id="workJourney.management.matrix.selectionHint" />
                  </div>
                }
              >
                <Button
                  type="text"
                  className={`gx-mb-0 ${styles.selectionHelpButton}`}
                  icon={<QuestionCircleOutlined />}
                  aria-label={intl.formatMessage({
                    id: 'workJourney.management.matrix.selectionHint.ariaLabel',
                  })}
                />
              </Popover>
            )}
          </div>
        </div>

        {configDeleted && (
          <p className={styles.hint}>
            <IntlMessages id="workJourney.management.matrix.deletedConfigNotice" />
          </p>
        )}

        {data && (
          <WorkJourneyMonthMatrixGrid
            days={data.days}
            users={data.users}
            cells={data.cells}
            coverage={data.coverage}
            daysOfWeek={daysOfWeek}
            selectedKeys={selectedKeys}
            onCellMouseDown={handleCellMouseDown}
            onCellMouseEnter={handleCellMouseEnter}
            onCellMouseUp={handleCellMouseUp}
            onCellClick={handleCellClick}
          />
        )}

        {shouldShowMatrixSelectionBar(selectionMeta) && selectedUser && !configDeleted && (
          <WorkJourneyMatrixSelectionBar
            cellCount={selectionMeta.count}
            userDisplayName={selectedUser.displayName}
            startDate={selectionMeta.startDate}
            endDate={selectionMeta.endDate}
            isContiguous={selectionMeta.isContiguous}
            coverOnly={selectionActions.isCoverOnlySelection}
            removeExceptionCopyKey={selectionActions.removeExceptionCopyKey}
            onAction={selectionActions.handleBarAction}
            onClear={clearSelection}
          />
        )}
      </BoxContainer>

      <WorkJourneyMatrixAttendanceDrawer
        open={attendanceOpen}
        loading={attendanceLoading}
        error={attendanceError}
        target={attendanceTarget}
        data={attendanceData}
        onClose={closeAttendanceDrawer}
        onEdit={configDeleted ? undefined : handleOpenEditAttendance}
      />

      <MatrixEditAttendanceDrawer
        open={editAttendanceOpen}
        data={editAttendanceData}
        organizationId={orgId}
        onClose={handleCloseEditAttendance}
        onSuccess={handleEditAttendanceSuccess}
      />

      <WorkJourneyMatrixAbsenceDrawer
        open={selectionActions.absenceDrawer.open}
        user={selectedUser}
        initialExcuse={selectionActions.absenceDrawer.excuse}
        initialStartDate={selectionMeta?.startDate ?? ''}
        initialEndDate={selectionMeta?.endDate ?? ''}
        hidePeriod={lockDrawerPeriodToSelection}
        selectionRanges={lockDrawerPeriodToSelection ? selectionExceptionRanges : undefined}
        submitting={selectionActions.submitting}
        onClose={selectionActions.closeAbsenceDrawer}
        onSubmit={selectionActions.handleAbsenceSubmit}
      />

      <WorkJourneyMatrixVacationDrawer
        open={selectionActions.vacationOpen}
        user={selectedUser}
        initialStartDate={selectionMeta?.startDate ?? ''}
        initialEndDate={selectionMeta?.endDate ?? ''}
        hidePeriod={lockDrawerPeriodToSelection}
        selectionRanges={lockDrawerPeriodToSelection ? selectionExceptionRanges : undefined}
        submitting={selectionActions.submitting}
        onClose={selectionActions.closeVacationDrawer}
        onSubmit={selectionActions.handleVacationSubmit}
      />

      <WorkJourneyMatrixReplaceDrawer
        open={selectionActions.replaceOpen}
        replacedUser={selectedUser}
        excludeUserIds={replacementExcludeUserIds}
        submitting={selectionActions.submitting}
        onClose={selectionActions.closeReplaceDrawer}
        onContinue={selectionActions.handleReplaceContinue}
      />

      <WorkJourneyMatrixDayInfoDrawer
        open={selectionActions.infoOpen}
        date={selectionActions.infoDate}
        userDisplayName={selectedUser?.displayName ?? '—'}
        userIdCard={selectedUser?.idCard}
        cell={selectionActions.infoCell}
        onClose={selectionActions.closeInfoDrawer}
        onUndoAbsence={selectionActions.handleUndoException}
        onUndoReplacement={selectionActions.handleUndoException}
      />

      {!configDeleted && (
        <PlanMonthlyJourneyDrawer
          open={planDrawerOpen}
          onClose={() => setPlanDrawerOpen(false)}
          initialWorkJourneyConfigId={workJourneyConfigId}
          onSaved={() => {
            clearSelection();
            loadMatrix();
          }}
        />
      )}
    </BoxContainer>
  );
};

export default WorkJourneyMonthMatrix;
